import { FeeType, Mission } from '@/shared/types/missions';

/**
 * Libellé d'affichage d'une mission, réutilisé dans l'historique des missions
 * et dans les lignes « Projet » du CRA (ex: "Optim.Net - CACIB").
 */
export function getMissionLabel(mission: Pick<Mission, 'title' | 'clientName'>): string {
  const client = mission.clientName?.trim();
  return client ? `${mission.title} - ${client}` : mission.title;
}

/**
 * Une mission est « active sur le mois » si sa période chevauche le mois affiché.
 * (startDate <= dernier jour du mois) ET (endDate absent OU >= premier jour du mois).
 * Une mission terminée reste ainsi visible sur ses mois passés et disparaît des mois
 * hors période. Réf. spec multi-mission (mobile → web).
 */
export function isMissionActiveInMonth(
  mission: Pick<Mission, 'startDate' | 'endDate'>,
  monthDate: Date,
): boolean {
  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const lastOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

  const start = new Date(mission.startDate);
  if (Number.isNaN(start.getTime())) return false;
  if (start > lastOfMonth) return false;

  if (mission.endDate) {
    const end = new Date(mission.endDate);
    if (!Number.isNaN(end.getTime()) && end < firstOfMonth) return false;
  }
  return true;
}

/**
 * Missions actives (par chevauchement) sur le mois affiché, triées par date de début.
 * C'est ce tableau qui alimente le `MissionSwitcher` : l'axe mission ne s'active qu'à
 * partir de 2 missions (`length > 1`).
 */
export function getMonthMissions<T extends Pick<Mission, 'startDate' | 'endDate'>>(
  missions: T[],
  monthDate: Date,
): T[] {
  return missions
    .filter(m => isMissionActiveInMonth(m, monthDate))
    .slice()
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
}

/** Parse `yyyy-mm-dd` en date locale (évite tout décalage de fuseau à l'affichage). */
function toLocalDate(iso: string): Date {
  const [y, mo, d] = iso.split('-').map(Number);
  return new Date(y, (mo || 1) - 1, d || 1);
}

/** Période d'une mission pour l'onglet (« 01/03/2026 → 31/03/2027 » ou « … → en cours »). */
export function formatMissionDateRange(
  startDate: string,
  endDate?: string | null,
): string | undefined {
  if (!startDate) return undefined;
  const s = toLocalDate(startDate).toLocaleDateString('fr-FR');
  const e = endDate ? toLocalDate(endDate).toLocaleDateString('fr-FR') : null;
  return e ? `${s} → ${e}` : `${s} → en cours`;
}

const TJM_CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CHF: 'CHF' };

/** Unité compacte du taux selon le type de facturation (pour le chip du switcher). */
const FEE_UNIT_COMPACT: Record<FeeType, string> = {
  [FeeType.DAY]: '/j',
  [FeeType.HOUR]: '/h',
  [FeeType.MONTH]: '/mois',
  [FeeType.FIXED]: ' forfait',
};

/**
 * TJM d'une mission formaté en version courte pour l'onglet du switcher (ex. « 1 200 €/j »).
 * Retourne `undefined` si le taux est absent/nul — utile quand le BE masque le TJM
 * (le chip est alors simplement omis).
 */
export function formatMissionTjm(
  mission: Pick<Mission, 'rate' | 'currency' | 'feeType'>,
): string | undefined {
  if (mission.rate == null || mission.rate <= 0) return undefined;
  // Les centimes, toujours : un TJM à 555 € et un TJM à 555,50 € ne se facturent pas pareil (#1074).
  const amount = mission.rate.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const symbol = TJM_CURRENCY_SYMBOL[mission.currency] ?? mission.currency;
  const unit = FEE_UNIT_COMPACT[mission.feeType] ?? '';
  return `${amount} ${symbol}${unit}`;
}

/** Slug stable dérivé du libellé de la mission (utilisé comme `value` d'option d'activité). */
export function getMissionActivityValue(mission: Pick<Mission, 'title' | 'clientName'>): string {
  return getMissionLabel(mission)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Titre de mission débarrassé du nom de client qui le préfixe.
 *
 * Les missions importées portent des titres du genre « KPC CRM - KPC CRM - Adobe Campaign »,
 * affichés juste sous le nom du client : la même chaîne apparaissait jusqu'à trois fois sur une
 * ligne de tableau, et c'est elle qui imposait sa largeur à la colonne — au point de rendre le
 * tableau scrollable horizontalement.
 *
 * On ne retire que ce qui est **strictement redondant** : le nom du client en tête, suivi d'un
 * séparateur. La répétition est traitée en boucle (le préfixe apparaît parfois deux fois). Si le
 * titre ne dit rien d'autre que le client, il est conservé tel quel — mieux vaut répéter que ne
 * plus rien afficher.
 */
export function missionTitleWithoutClient(
  title?: string | null,
  clientName?: string | null,
): string {
  const base = (title ?? '').trim();
  const client = (clientName ?? '').trim();
  if (!base || !client) return base;

  const separator = /^[\s]*[-–—·/|:][\s]*/;
  let out = base;
  // Plusieurs passes : « CLIENT - CLIENT - vrai titre » se rencontre à l'import.
  for (let i = 0; i < 3; i++) {
    if (out.toLowerCase().indexOf(client.toLowerCase()) !== 0) break;
    const rest = out.slice(client.length);
    const trimmed = rest.replace(separator, '');
    // Sans séparateur derrière, ce n'est pas un préfixe : « ATRIUM » ne préfixe pas « ATRIUMS ».
    if (trimmed === rest || !trimmed.trim()) break;
    out = trimmed.trim();
  }
  return out || base;
}
